import { splitHtmlByPageBreaks } from "./utils";

/** Matches printable program page content area (sheet minus padding/header/title). */
export const PAGE_CONTENT_HEIGHT = 780;
const MEASURE_WIDTH_PX = 692; // 780 sheet - horizontal padding

function isContentNode(node) {
  if (node.nodeType === Node.TEXT_NODE) return String(node.textContent || "").trim().length > 0;
  if (node.nodeType === Node.ELEMENT_NODE) return true;
  return false;
}

function packSegment(host, segmentHtml, { titleReserve = 0, htmlClassName = "" } = {}) {
  host.innerHTML = "";
  const measure = document.createElement("div");
  if (htmlClassName) measure.className = htmlClassName;
  measure.style.width = `${MEASURE_WIDTH_PX}px`;
  measure.innerHTML = segmentHtml;
  host.appendChild(measure);

  const blocks = [...measure.childNodes].filter(isContentNode);
  if (!blocks.length) {
    host.innerHTML = "";
    return segmentHtml.trim() ? [segmentHtml] : [];
  }

  let available = PAGE_CONTENT_HEIGHT - titleReserve;
  const pages = [];
  let current = document.createElement("div");

  const flush = () => {
    const html = current.innerHTML.trim();
    if (html) pages.push(html);
    current = document.createElement("div");
    available = PAGE_CONTENT_HEIGHT;
  };

  blocks.forEach((block) => {
    const clone = block.cloneNode(true);
    current.appendChild(clone);
    host.innerHTML = "";
    const probe = document.createElement("div");
    if (htmlClassName) probe.className = htmlClassName;
    probe.style.width = `${MEASURE_WIDTH_PX}px`;
    probe.innerHTML = current.innerHTML;
    host.appendChild(probe);
    const height = probe.scrollHeight;

    if (height > available && current.childNodes.length > 1) {
      current.removeChild(current.lastChild);
      flush();
      current.appendChild(clone);
    } else if (height > available && current.childNodes.length === 1) {
      flush();
    }
  });

  const trailing = current.innerHTML.trim();
  if (trailing) pages.push(trailing);

  host.innerHTML = "";
  return pages;
}

/**
 * Split on explicit <hr> page breaks, then pack each segment into fixed-height pages.
 * @returns {string[]} HTML chunks, one per printable sheet
 */
export function packProgramPageHtml(html, measureHost, { hasTitle = false, htmlClassName = "" } = {}) {
  const source = String(html || "").trim();
  if (!source) return [];

  const segments = splitHtmlByPageBreaks(source);
  if (!segments.length) return [];

  if (!measureHost || typeof document === "undefined") {
    return segments;
  }

  const packed = [];
  segments.forEach((segmentHtml, segmentIndex) => {
    const titleReserve = hasTitle && segmentIndex === 0 ? 110 : 0;
    const pages = packSegment(measureHost, segmentHtml, { titleReserve, htmlClassName });
    if (pages.length) packed.push(...pages);
    else if (segmentHtml.trim()) packed.push(segmentHtml);
  });

  return packed.length ? packed : segments;
}

/**
 * Bind image load listeners so callers can re-pack when remote images finish loading.
 * Cached/complete images must NOT notify — attaching load handlers before checking
 * `complete` caused an unbounded re-pack loop (multi-GB Chrome tabs).
 */
export function watchImagesForRepack(html, onLoad) {
  if (typeof document === "undefined" || typeof onLoad !== "function") return () => {};
  const container = document.createElement("div");
  container.innerHTML = String(html || "");
  const images = [...container.querySelectorAll("img")].filter((img) => img.getAttribute("src"));
  // Skip data:/blob: URLs — decoding them into Image bitmaps duplicates huge payloads in RAM.

  let cancelled = false;
  let debounceTimer = null;
  const notify = () => {
    if (cancelled) return;
    if (debounceTimer) clearTimeout(debounceTimer);
    // Coalesce many image loads into a single re-pack.
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      if (!cancelled) onLoad();
    }, 50);
  };

  const cleanups = images.map((imgEl) => {
    const src = imgEl.getAttribute("src");
    if (!src || src.startsWith("data:") || src.startsWith("blob:")) return () => {};

    const img = new Image();
    // Set src before listeners so browser-cache hits are detected via `complete`
    // and never schedule another pack.
    img.src = src;
    if (img.complete) {
      return () => {
        img.src = "";
      };
    }

    const handler = () => notify();
    img.addEventListener("load", handler, { once: true });
    img.addEventListener("error", handler, { once: true });
    // Rare race: finished between the complete check and listener attach.
    if (img.complete) {
      img.removeEventListener("load", handler);
      img.removeEventListener("error", handler);
      notify();
      return () => {
        img.src = "";
      };
    }
    return () => {
      img.removeEventListener("load", handler);
      img.removeEventListener("error", handler);
      img.src = "";
    };
  });

  return () => {
    cancelled = true;
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    cleanups.forEach((fn) => fn && fn());
  };
}
